/**
 * Phase 3: Optimized Dashboard Data Hook
 * 
 * Reduced over-selection of auth state and optimized selector usage
 * to minimize re-render propagation to dashboard children.
 */

import { useSelector } from 'react-redux';
import { selectCurrentUser, selectUserRoles, selectIsAuthenticated, selectCanManageUsersJWT } from '../features/auth/authSlice';
import { useClaimsQuery, useUsersQuery } from '../services/queryService';
import { useMemo } from 'react';

/**
 * Hook that provides centralized access to dashboard data
 * 
 * Phase 3: Optimized to minimize re-renders by returning only primitives
 * and memoizing derived values to prevent unnecessary child updates.
 */
export const useDashboardData = () => {
  // Auth data from Redux - using specific selectors to minimize re-renders
  const userId = useSelector(state => state.auth.user?.userId);
  const username = useSelector(state => state.auth.user?.username);
  const userRoles = useSelector(selectUserRoles);
  const isAuthLoaded = useSelector(selectIsAuthenticated);
  const canManageUsers = useSelector(selectCanManageUsersJWT);
  
  // Claims data from centralized query service
  const { 
    data: claimsResponse, 
    isLoading: isClaimsLoading, 
    isError: isClaimsError 
  } = useClaimsQuery();
  
  // Extract claims array from the service response
  const claims = claimsResponse?.claims || [];
  
  // Users data from centralized query service (only if user has permission)
  const { 
    data: users, 
    isLoading: isUsersLoading, 
    isError: isUsersError 
  } = useUsersQuery();

  // Memoize derived values to prevent object recreation
  const dashboardCounts = useMemo(() => ({
    claimsCount: claims.length || 0,
    usersCount: canManageUsers ? (users?.length || 0) : 0,
    hasData: isAuthLoaded && (!!claims || !isClaimsLoading)
  }), [claims.length, users?.length, canManageUsers, isAuthLoaded, isClaimsLoading]);

  // Memoize loading states
  const loadingStates = useMemo(() => ({
    isClaimsLoading,
    isUsersLoading: canManageUsers ? isUsersLoading : false,
    isAnyLoading: isClaimsLoading || (canManageUsers && isUsersLoading)
  }), [isClaimsLoading, isUsersLoading, canManageUsers]);

  // Memoize error states  
  const errorStates = useMemo(() => ({
    isClaimsError,
    isUsersError: canManageUsers ? isUsersError : false,
    hasAnyError: isClaimsError || (canManageUsers && isUsersError)
  }), [isClaimsError, isUsersError, canManageUsers]);

  // Return minimal primitives and memoized objects to prevent re-renders
  return {
    // Primitive auth data
    userId,
    username,
    userRoles,
    isAuthLoaded,
    canManageUsers,
    
    // Memoized data counts
    ...dashboardCounts,
    
    // Memoized loading states
    ...loadingStates,
    
    // Memoized error states  
    ...errorStates,
    
    // Raw data only when needed
    claims: claims || [],
    users: canManageUsers ? (users || []) : [],
    
    // Legacy compatibility (deprecated - use primitives above)
    user: { userId, username }, // Minimal user object
    isLoading: loadingStates.isAnyLoading,
    error: errorStates.hasAnyError ? 'Failed to load dashboard data' : null
  };
};
